import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, IndianRupee } from "lucide-react";

const ServicesPage = () => {
  const { API, getAuthHeader, user } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterType, setFilterType] = useState("all");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "package",
    type: "domestic",
    tier: "",
    price: "",
    inclusions: "",
  });

  useEffect(() => {
    fetchServices();
  }, [filterCategory, filterType]);

  const fetchServices = async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory !== "all") params.append("category", filterCategory);
      if (filterType !== "all") params.append("type", filterType);
      
      const response = await axios.get(`${API}/services?${params}`, getAuthHeader());
      setServices(response.data);
    } catch (error) {
      console.error("Fetch services error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        inclusions: formData.inclusions.split("\n").filter(Boolean),
        tier: formData.category === "package" ? formData.tier : null,
      };

      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, payload, getAuthHeader());
        toast.success("Service updated successfully");
      } else {
        await axios.post(`${API}/services`, payload, getAuthHeader());
        toast.success("Service created successfully");
      }

      setDialogOpen(false);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save service");
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      category: service.category,
      type: service.type,
      tier: service.tier || "",
      price: service.price.toString(),
      inclusions: service.inclusions?.join("\n") || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    try {
      await axios.delete(`${API}/services/${serviceId}`, getAuthHeader());
      toast.success("Service deleted successfully");
      fetchServices();
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      name: "",
      description: "",
      category: "package",
      type: "domestic",
      tier: "",
      price: "",
      inclusions: "",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const canManage = user?.role === "admin" || user?.role === "manager";

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-950" data-testid="services-title">
            Services
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Manage airport service packages and individual services
          </p>
        </div>
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0A192F] hover:bg-zinc-800 rounded-sm" data-testid="add-service-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Service
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingService ? "Edit Service" : "Add New Service"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider">Category</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                      <SelectTrigger data-testid="service-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="package">Package</SelectItem>
                        <SelectItem value="individual">Individual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider">Type</Label>
                    <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                      <SelectTrigger data-testid="service-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="domestic">Domestic</SelectItem>
                        <SelectItem value="international">International</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {formData.category === "package" && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider">Tier</Label>
                    <Select value={formData.tier} onValueChange={(v) => setFormData({...formData, tier: v})}>
                      <SelectTrigger data-testid="service-tier-select">
                        <SelectValue placeholder="Select tier" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="silver">Silver</SelectItem>
                        <SelectItem value="gold">Gold</SelectItem>
                        <SelectItem value="elite">Elite</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Service name"
                    required
                    data-testid="service-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Service description"
                    rows={2}
                    data-testid="service-description-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Price (INR)</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="0"
                    required
                    data-testid="service-price-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Inclusions (one per line)</Label>
                  <Textarea
                    value={formData.inclusions}
                    onChange={(e) => setFormData({...formData, inclusions: e.target.value})}
                    placeholder="Welcome from Curbside&#10;Porter assistance&#10;..."
                    rows={4}
                    data-testid="service-inclusions-input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#0A192F] hover:bg-zinc-800" data-testid="service-save-button">
                    {editingService ? "Update" : "Create"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-36" data-testid="filter-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="package">Packages</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-36" data-testid="filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="domestic">Domestic</SelectItem>
                  <SelectItem value="international">International</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Services Table */}
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50/50">
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Service</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Category</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Type</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Tier</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Price</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Status</TableHead>
                {canManage && <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                    Loading services...
                  </TableCell>
                </TableRow>
              ) : services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                    No services found
                  </TableCell>
                </TableRow>
              ) : (
                services.map((service) => (
                  <TableRow key={service.id} className="hover:bg-zinc-50" data-testid={`service-row-${service.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-zinc-100 flex items-center justify-center">
                          <Package className="w-4 h-4 text-zinc-600" />
                        </div>
                        <div>
                          <p className="font-medium text-zinc-900">{service.name}</p>
                          <p className="text-xs text-zinc-500 truncate max-w-xs">{service.description}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs capitalize">
                        {service.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize text-sm">{service.type}</TableCell>
                    <TableCell>
                      {service.tier ? (
                        <Badge
                          className={`text-xs capitalize ${
                            service.tier === "elite"
                              ? "bg-amber-100 text-amber-700"
                              : service.tier === "gold"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-zinc-100 text-zinc-700"
                          }`}
                        >
                          {service.tier}
                        </Badge>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{formatCurrency(service.price)}</TableCell>
                    <TableCell>
                      <Badge className={service.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                        {service.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(service)} data-testid={`edit-service-${service.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(service.id)} data-testid={`delete-service-${service.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServicesPage;
